import { useCallback, useEffect, useRef, useState } from "react";
import * as signalR from "@microsoft/signalr";
import { type HubConnection, HubConnectionState, type IHttpConnectionOptions } from "@microsoft/signalr";
import { getUnmanagedBearerToken } from "@packsize/authentication";

import { handleUnauthorizedFailure } from "@/lib/auth-failure";

type UseSignalRHubParams = {
	hubUrl: string;
	token?: string | null;
	logLevel?: signalR.LogLevel;
	onConnected?: () => void;
	onDisconnected?: (error?: Error) => void;
	onReconnecting?: (error?: Error) => void;
	onReconnected?: (connectionId?: string) => void;
	debug?: boolean;
};

type SignalREventHandler = (...args: unknown[]) => void;

const normalizeBearerToken = (rawToken?: string | null) => {
	if (!rawToken) {
		return "";
	}

	return rawToken.replace(/^\s*bearer\s+/i, "").trim();
};

const debugLog = (debug: boolean, message: string, ...args: unknown[]) => {
	if (debug || import.meta.env.VITE_SIGNALR_DEBUG === "true") {
		console.log(`[SignalR Debug] ${message}`, ...args);
	}
};

export const useSignalRHub = ({
	hubUrl,
	token,
	logLevel = signalR.LogLevel.Warning,
	onConnected,
	onDisconnected,
	onReconnecting,
	onReconnected,
	debug = false,
}: UseSignalRHubParams) => {
	const [hubConnection, setHubConnection] = useState<HubConnection | undefined>(undefined);
	const [hubConnectionEstablished, setHubConnectionEstablished] = useState<boolean>(false);
	const [usePolling, setUsePolling] = useState<boolean>(true);

	// Keep refs of callbacks to avoid re-triggering connection logic when they change
	const onConnectedRef = useRef(onConnected);
	const onDisconnectedRef = useRef(onDisconnected);
	const onReconnectingRef = useRef(onReconnecting);
	const onReconnectedRef = useRef(onReconnected);
	const tokenRef = useRef(token);

	useEffect(() => {
		tokenRef.current = token;
	}, [token]);

	useEffect(() => {
		onConnectedRef.current = onConnected;
		onDisconnectedRef.current = onDisconnected;
		onReconnectingRef.current = onReconnecting;
		onReconnectedRef.current = onReconnected;
	}, [onConnected, onDisconnected, onReconnecting, onReconnected]);

	useEffect(() => {
		if (!token) {
			return;
		}

		let isActive = true;

		debugLog(debug, "Creating SignalR connection", { hubUrl, logLevel });

		const options: IHttpConnectionOptions = {
			transport: signalR.HttpTransportType.WebSockets,
			logger: logLevel,
			accessTokenFactory: () => normalizeBearerToken(getUnmanagedBearerToken() ?? tokenRef.current),
		};

		const connection = new signalR.HubConnectionBuilder()
			.withUrl(hubUrl, options)
			.withAutomaticReconnect({
				nextRetryDelayInMilliseconds: (retryContext) => {
					if (handleUnauthorizedFailure(retryContext.retryReason)) {
						debugLog(
							debug,
							"Stopping SignalR reconnect attempts due to authentication failure",
							retryContext.retryReason,
						);
						return null;
					}

					return [0, 2000, 10000, 30000][retryContext.previousRetryCount] ?? null;
				},
			})
			.configureLogging(logLevel)
			.build();

		setHubConnection(connection);

		connection
			.start()
			.then(() => {
				if (!isActive) return;

				debugLog(debug, "SignalR connection established");
				setHubConnectionEstablished(true);
				setUsePolling(false);
				onConnectedRef.current?.();
			})
			.catch((error) => {
				if (!isActive) return;
				debugLog(debug, "SignalR connection error", error);
				if (handleUnauthorizedFailure(error)) {
					debugLog(debug, "SignalR initial connect failed due to authentication failure");
					return;
				}
				console.error(`Error establishing SignalR connection to ${hubUrl}:`, error);
			});

		connection.onclose((error) => {
			if (!isActive) return;
			debugLog(debug, "SignalR connection closed", error);
			setHubConnectionEstablished(false);
			setUsePolling(true);
			onDisconnectedRef.current?.(error);
		});

		connection.onreconnecting((error) => {
			if (!isActive) return;
			debugLog(debug, "SignalR connection reconnecting", error);
			setHubConnectionEstablished(false);
			setUsePolling(true);
			onReconnectingRef.current?.(error);
		});

		connection.onreconnected((connectionId) => {
			if (!isActive) return;
			debugLog(debug, "SignalR connection reconnected", connectionId);
			setHubConnectionEstablished(true);
			setUsePolling(false);
			onReconnectedRef.current?.(connectionId);
		});

		return () => {
			isActive = false;
			debugLog(debug, "Stopping SignalR connection cleanup");
			connection.stop()?.catch((e) => {
				debugLog(debug, "Error stopping SignalR connection", e);
			});
			setHubConnectionEstablished(false);
			setHubConnection(undefined);
			setUsePolling(true);
		};
	}, [hubUrl, token, logLevel, debug]);

	const invoke = useCallback(
		async <T = void>(methodName: string, ...args: unknown[]): Promise<T | null> => {
			if (!hubConnection || hubConnection.state !== HubConnectionState.Connected) {
				debugLog(debug, `Cannot invoke ${methodName}: connection not established`);
				return null;
			}

			try {
				debugLog(debug, `Invoking ${methodName}`, args);
				return await hubConnection.invoke<T>(methodName, ...args);
			} catch (error) {
				console.error(`Error invoking ${methodName}:`, error);
				return null;
			}
		},
		[hubConnection, debug],
	);

	const subscribe = useCallback(
		(eventName: string, handler: SignalREventHandler) => {
			if (!hubConnection) {
				debugLog(debug, `Cannot subscribe to ${eventName}: no connection obj`);
				return;
			}

			debugLog(debug, `Subscribing to ${eventName}`);
			hubConnection.on(eventName, handler);
		},
		[hubConnection, debug],
	);

	const unsubscribe = useCallback(
		(eventName: string, handler?: SignalREventHandler) => {
			if (!hubConnection) {
				return;
			}

			debugLog(debug, `Unsubscribing from ${eventName}`);
			if (handler) {
				hubConnection.off(eventName, handler);
			} else {
				hubConnection.off(eventName);
			}
		},
		[hubConnection, debug],
	);

	return {
		hubConnection,
		hubConnectionEstablished,
		usePolling,
		invoke,
		subscribe,
		unsubscribe,
	};
};
